import DataBase.UINKDBInterface
import Structs.Api.ApiNote
import Structs.ClientCarteset
import Structs.ClientPay
import Structs.ClientTaxNote
import Structs.ClientTaxNoteData
import Structs.ClientStaticData
import Structs.DeliveryNote
import Structs.InformationBase
import Structs.Note
import Structs.OrderNote
import Structs.PaymentData
import Structs.VisitNote
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlinx.serialization.json.putJsonObject

/**
 * JSON boundary for the nexilcolab web app (modernization roadmap, Stage 3).
 *
 * Serializes a document header (any [InformationBase] subtype the web app's
 * `deserializeDocument` understands) into ONE JSON string containing every
 * field the web deserializers used to read member-by-member off the live
 * Kotlin object. Each JSON field maps 1:1 to a previous direct call, so the
 * web app no longer depends on Kotlin member names surviving compilation.
 *
 * Entity name resolution (client/supplier display + business name + includeTax)
 * is done HERE, with full DB access, instead of in the web app.
 */
object DocumentJsonExport {

    // Float.NaN / Infinity (corrupt or edge data) serialize as the literal
    // tokens NaN/Infinity, which are NOT valid JSON and make JSON.parse throw
    // on the web side (killing the whole document list). Replace any non-finite
    // number with 0 so the bundle always emits parseable JSON.
    private fun sanitizeNonFinite(e: JsonElement): JsonElement = when (e) {
        is JsonObject -> JsonObject(e.mapValues { sanitizeNonFinite(it.value) })
        is JsonArray -> JsonArray(e.map { sanitizeNonFinite(it) })
        is JsonPrimitive ->
            if (!e.isString && (e.content == "NaN" || e.content == "Infinity" || e.content == "-Infinity"))
                JsonPrimitive(0)
            else e
        else -> e
    }

    private fun apiNote(n: ApiNote?): JsonObject? = n?.let {
        buildJsonObject {
            put("send", it.send)
            put("reason", it.reason)
        }
    }

    private fun payment(p: PaymentData): JsonObject = buildJsonObject {
        put("payMethod", p.payMethod.name)
        put("value", p.value)
        put("check_date", p.check_date)
        put("check_number", p.check_number)
        put("bankName", p.bankName())
        put("bank_branch", p.bank_branch)
        put("bank_number", p.bank_number)
        put("notes", p.notes)
        put("category", p.category)
        put("paid", p.paid)
        put("paid_date", p.paid_date)
        put("paid_details", p.paid_details)
        put("canceled", p.canceled)
        if (p.inner_id > 0) put("inner_id", p.inner_id)
    }

    private fun details(sd: ClientStaticData): JsonObject = buildJsonObject {
        put("id", sd.id)
        put("business_name", sd.business_name)
        put("business_id", sd.business_id)
        put("city", sd.city)
        put("street", sd.street)
        put("streetNumber", sd.streetNumber)
        put("phone", sd.phone)
        put("supplier_input_equipment", sd.supplier_input_equipment)
    }

    private fun taxLine(l: ClientTaxNoteData?): JsonObject? = l?.let {
        buildJsonObject {
            put("value", it.value)
            put("taxPaid", it.taxPaid)
            put("details", it.details)
            put("amount", it.amount)
        }
    }

    fun toJson(d: InformationBase): String {
        val db = UINKDBInterface.activeDB
        val docType = d.getConnectedDocType()
        val connectedDate = d.getConnectedDate()

        // Per-type entity resolution (mirrors what utils.ts deserializers did).
        val isClient: Boolean = when (d) {
            is ClientTaxNote -> d.isClient
            is ClientPay -> d.isClient
            is Note -> docType != 6
            is ClientCarteset -> docType != 20
            else -> true
        }
        val entLookupId: Int = when (d) {
            is ClientTaxNote -> d.client_id
            is ClientPay -> d.client_id
            is Note -> d.ent_id
            else -> d.getConnectedEntId()
        }
        val ent = if (isClient) db.getClient(entLookupId).first else db.getSupplier(entLookupId).first

        val json = buildJsonObject {
            put("docType", docType)
            put("id", d.getConnectedId())
            put("displayDocId", d.displayDocId())
            put("entId", d.getConnectedEntId())
            put("connectedDate", connectedDate)
            put("connectedDateTime", d.getConnectedDateTime())
            put("used", d.isUsed())
            put("active", d.isActive())
            put("sign", d.digiSign())
            put("photo", d.digiPhoto())
            put("photoCount", d.photoCount())
            put("isClient", isClient)
            put("totalAmount", d.getAmounts())
            put("entName", ent?.getName(connectedDate))
            put("entBName", ent?.getBusinessName(connectedDate))
            // web app always called getIncludeTax() with no date (= today)
            put("includeTax", ent?.getIncludeTax())
            put("hasEnt", ent != null)
            put("storeNext", apiNote(d.getStoreNext()) ?: JsonNull)
            put("manager", apiNote(d.getManager()) ?: JsonNull)
            put("hashavshevet", apiNote(d.getHashavshevet()) ?: JsonNull)
            put("one", apiNote(d.getOne()) ?: JsonNull)
            put("sentToExternalSoftware", d.isSentToExternalSoftware())
            put("externalSoftware", apiNote(d.getExternalSoftwareNote()) ?: JsonNull)

            when (d) {
                is ClientTaxNote -> {
                    put("kind", "taxNote")
                    put("date", d.date)
                    put("agent", d.agent)
                    put("value", d.value)
                    put("totalValue", d.total_value)
                    put("valueLeft", d.value_left)
                    put("taxPaid", d.getTaxPaid())
                    put("discountPercent", d.discount_percent)
                    put("externalDetails", d.external_details)
                    put("taxConfirmation", d.taxConfirmation)
                    put("supplier_invoice_number", d.supplierInvoiceNumber())
                    put("taxNoteTypeName", d.taxNoteType.name)
                    put("pdf", d.pdf)
                    // Generic createDocument fields the old code read off nkObject
                    // for ALL types — ClientTaxNote has them, so export here too.
                    put("order_id", d.order_id)
                    put("agent_id", d.agent_id)
                    put("notes2", d.notes2)
                    put("notes3", d.notes3)
                    put("notes4", d.notes4)
                    put("notes5", d.notes5)
                    put("driverName", d.getDriver()?.user_name)
                    put("collectorName", d.getCollector()?.user_name)
                    put("hasProducts", d.productDeliveries.isNotEmpty())
                    putJsonArray("payments") { d.paymentsData.forEach { add(payment(it)) } }
                    put("hand", taxLine(d.getHandValueOrNull()) ?: JsonNull)
                    put("round", taxLine(d.getRoundValueOrNull()) ?: JsonNull)
                    putJsonArray("freeLines") { d.getFreeValueLinesOrNull().forEach { add(taxLine(it)!!) } }
                    putJsonArray("deliveryIds") { (if (d.hasNotes()) d.getNotes() else listOf()).forEach { add(kotlinx.serialization.json.JsonPrimitive(it)) } }
                    put("details", details(d.ClientStaticData))
                }
                is ClientPay -> {
                    put("kind", "payNote")
                    put("date", d.date)
                    put("agent", d.agent)
                    put("value", d.value)
                    put("externalDetails", d.external_details)
                    put("refId", d.ref_id)
                    put("driverName", d.getDriver()?.user_name)
                    putJsonArray("payments") { d.paymentsData.forEach { add(payment(it)) } }
                    putJsonArray("taxNotes") { d.taxNotes.forEach { add(kotlinx.serialization.json.JsonPrimitive(it)) } }
                    putJsonArray("taxNotesDisplay") { d.displayTaxNoteIds().forEach { add(kotlinx.serialization.json.JsonPrimitive(it)) } }
                    put("details", details(d.ClientStaticData))
                }
                is Note -> {
                    put("kind", "deliveryNote")
                    put("dateIssued", d.date_issued)
                    put("agent", d.agent)
                    put("agent_id", d.agent_id)
                    put("value", d.value)
                    put("taxPaid", d.taxPaid)
                    put("notes", d.notes)
                    put("notes2", d.notes2)
                    put("notes3", d.notes3)
                    put("notes4", d.notes4)
                    put("notes5", d.notes5)
                    put("bul", d.connected_id)
                    put("entIdRaw", d.ent_id)
                    put("onlyReturns", d.isOnlyReturns())
                    put("driver_id", d.driver_id)
                    put("collector_id", d.collector_id)
                    put("hasHistory", d.histroy_info.isNotEmpty())
                    when (d) {
                        is DeliveryNote -> {
                            put("pdf", d.pdf)
                            put("order_id", d.order_id)
                            put("driverName", d.getDriver()?.user_name)
                            put("collectorName", d.getCollector()?.user_name)
                        }
                        is OrderNote -> {
                            put("category", d.category)
                            put("orderStatusName", d.orderStatus.orderName())
                            put("notes6", d.notes6)
                            put("notes7", d.notes7)
                            put("notes8", d.notes8)
                            put("notes9", d.notes9)
                            put("notes10", d.notes10)
                            put("refId", d.ref_id)
                            put("order_id", d.order_id)
                            put("row_version", d.row_version)
                            put("driverName", d.getDriver()?.user_name)
                            put("collectorName", d.getCollector()?.user_name)
                        }
                        else -> {}
                    }
                }
                is ClientCarteset -> {
                    put("kind", "debt")
                    put("date", d.date)
                    put("agent", d.getConnectedAgent())
                    put("notes", d.notes)
                    put("value", d.getConnectedValue())
                    put("positive", d.positive)
                    put("negative", d.negative)
                    put("state", d.state)
                }
                is VisitNote -> {
                    put("kind", "visitNote")
                    put("dateIssued", d.date_issued)
                    put("order_id", d.order_id)
                    put("driverName", d.getDriver()?.user_name)
                    put("agent", d.getConnectedAgent())
                    put("comment", d.comment)
                    put("value", d.getConnectedValue())
                    put("amounts", d.getAmounts())
                    put("connectedPayment", d.getConnectedPayment())
                    put("hasConnectedProducts", d.getConnectedProducts() != null)
                    val p = d.getConnectedProduct()
                    if (p != null) putJsonObject("product") {
                        put("id", p.id)
                        put("name", p.name)
                    } else put("product", JsonNull)
                    putJsonArray("states") { d.getStatesStr().forEach { add(kotlinx.serialization.json.JsonPrimitive(it)) } }
                }
                else -> {
                    put("kind", "unknown")
                }
            }
        }
        return sanitizeNonFinite(json).toString()
    }
}
